import React, { useState, useEffect } from 'react';
import { SQUARE_CONFIG, SQUARE_FORM_STYLES } from '../config/squareConfig';
import { processSquarePayment, getSavedCreditCards } from '../api/squareApi';
import { getCreditCardsByPassengerId } from '../api/getCreditCards';

const SquarePayment = ({ 
  bookingRefId, 
  amount, 
  passengerId,
  paymentType = 'FULL', // 'FULL' or 'PARTIAL'
  onSuccess, 
  onError,
  onLoading 
}) => {
  const [card, setCard] = useState(null);
  const [cardElements, setCardElements] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('new'); // 'new' or 'saved'
  const [errors, setErrors] = useState({});
  const [loadingCards, setLoadingCards] = useState(false);
  const [cardHolderName, setCardHolderName] = useState('');
  const [saveCard, setSaveCard] = useState(false);

  useEffect(() => {
    initializeSquare();
    if (passengerId) {
      loadSavedCards();
    }
  }, [passengerId]);

  useEffect(() => {
    if (onLoading) {
      onLoading(isLoading);
    }
  }, [isLoading, onLoading]);

  const initializeSquare = async () => {
    try {
      // Check if Square is available
      if (!window.Square) {
        throw new Error('Square Web Payments SDK not loaded');
      }

      const payments = window.Square.payments(
        SQUARE_CONFIG.applicationId, 
        SQUARE_CONFIG.locationId
      );
      
      // Enhanced styling for individual card elements
      const elementStyles = {
        '.input-container': {
          borderColor: 'transparent',
          borderRadius: '0px',
          borderWidth: '0px',
          backgroundColor: 'transparent',
          fontSize: '16px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          padding: '12px 16px',
          color: '#374151'
        },
        '.input-container.is-focus': {
          borderColor: 'transparent',
          boxShadow: 'none',
          outline: 'none'
        },
        '.input-container.is-error': {
          borderColor: 'transparent',
          boxShadow: 'none'
        },
        '.message-text': {
          color: '#ef4444',
          fontSize: '14px',
          marginTop: '4px'
        }
      };
      
      // Initialize individual card elements
      const cardNumber = await payments.card({
        style: elementStyles
      });
      
      const expirationDate = await payments.expirationDate({
        style: elementStyles
      });
      
      const cvv = await payments.cvv({
        style: elementStyles
      });
      
      const postalCode = await payments.postalCode({
        style: elementStyles
      });
      
      // Attach elements to their containers
      await cardNumber.attach('#card-container');
      await expirationDate.attach('#expiry-container');
      await cvv.attach('#cvv-container');
      await postalCode.attach('#postal-code-container');
      
      setCard(cardNumber);
      
      // Store all card elements
      setCardElements({
        cardNumber,
        expirationDate,
        cvv,
        postalCode
      });
      
    } catch (error) {
      console.error('Square initialization failed:', error);
      setErrors({ init: 'Payment form initialization failed. Please refresh the page.' });
    }
  };

  const loadSavedCards = async () => {
    try {
      setLoadingCards(true);
      console.log('[SquarePayment] Loading saved cards for passenger:', passengerId);
      const cards = await getCreditCardsByPassengerId(passengerId);
      console.log('[SquarePayment] Saved cards loaded:', cards);
      setSavedCards(cards?.data || cards || []);
    } catch (error) {
      console.error('[SquarePayment] Error loading saved cards:', error);
      setSavedCards([]);
    } finally {
      setLoadingCards(false);
    }
  };

  const handleSquareErrors = (error) => {
    const errorMessages = {
      'VALIDATION_ERROR': 'Please check your card details',
      'CARD_DECLINED': 'Your card was declined. Please try another card',
      'INSUFFICIENT_FUNDS': 'Insufficient funds. Please try another card',
      'GENERIC_DECLINE': 'Payment was declined. Please contact your bank',
      'INVALID_CARD': 'Invalid card number. Please check and try again',
      'EXPIRED_CARD': 'Your card has expired. Please use a different card',
      'CVV_FAILURE': 'Invalid CVV. Please check the security code',
      'ADDRESS_VERIFICATION_FAILURE': 'Address verification failed',
      'PROCESSING_ERROR': 'Payment processing error. Please try again'
    };

    return errorMessages[error.type] || error.message || 'Payment processing failed';
  };

  const processPayment = async (token) => {
    const paymentAmount = paymentType === 'PARTIAL' ? '1.00' : parseFloat(amount).toFixed(2);
    
    // Format payment data exactly per backend API requirements
    const paymentData = {
      booking_ref_id: bookingRefId,
      amount: paymentAmount, // Already formatted as string with 2 decimals
      card_token: token,
      card_holder_name: cardHolderName || '',
      is_save_cc: saveCard ? '1' : '0'
    };

    console.log('[SquarePayment] Step 3: Processing new card payment with backend format:', JSON.stringify(paymentData, null, 2));

    try {
      const result = await processSquarePayment(paymentData);
      
      console.log('[SquarePayment] Payment result:', result);
      
      if (result.status === 1 || result.success) {
        console.log('[SquarePayment] Payment successful');
        onSuccess({
          ...result,
          paymentMethod: 'New Card',
          transactionId: result.transaction_id || result.payment_id
        });
      } else {
        throw new Error(result.msg || result.message || 'Payment failed');
      }
    } catch (error) {
      console.error('[SquarePayment] Payment error:', error);
      throw new Error(error.message || 'Payment processing failed');
    }
  };

  const processPaymentWithSavedCard = async (cardId) => {
    const paymentAmount = paymentType === 'PARTIAL' ? '1.00' : parseFloat(amount).toFixed(2);
    
    // Format saved card payment data per backend API requirements
    const paymentData = {
      booking_ref_id: bookingRefId,
      amount: paymentAmount, // Already formatted as string with 2 decimals
      creditcard_id: cardId
    };

    console.log('[SquarePayment] Processing saved card payment with backend format:', JSON.stringify(paymentData, null, 2));

    try {
      const result = await processSquarePayment(paymentData);
      
      console.log('[SquarePayment] Saved card payment result:', result);
      
      if (result.status === 1 || result.success) {
        console.log('[SquarePayment] Saved card payment successful');
        onSuccess({
          ...result,
          paymentMethod: 'Saved Card',
          transactionId: result.transaction_id || result.payment_id
        });
      } else {
        throw new Error(result.msg || result.message || 'Payment failed');
      }
    } catch (error) {
      console.error('[SquarePayment] Saved card payment error:', error);
      throw new Error(error.message || 'Payment processing failed');
    }
  };

  const handlePayment = async () => {
    if (paymentMethod === 'new' && !card) {
      setErrors({ payment: 'Payment form not initialized. Please refresh the page.' });
      return;
    }

    if (paymentMethod === 'saved' && !selectedCardId) {
      setErrors({ payment: 'Please select a saved card or use a new card' });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      console.log('[SquarePayment] Starting payment process with method:', paymentMethod);
      
      if (paymentMethod === 'saved') {
        await processPaymentWithSavedCard(selectedCardId);
      } else {
        // Tokenize the new card using Square Web Payments SDK
        console.log('[SquarePayment] Tokenizing new card...');
        const tokenResult = await card.tokenize();
        
        if (tokenResult.status === 'OK') {
          console.log('[SquarePayment] Card tokenization successful');
          await processPayment(tokenResult.token);
        } else {
          const errorMessage = tokenResult.errors?.[0] 
            ? handleSquareErrors(tokenResult.errors[0])
            : 'Card tokenization failed';
          console.error('[SquarePayment] Tokenization failed:', errorMessage);
          throw new Error(errorMessage);
        }
      }
    } catch (error) {
      console.error('[SquarePayment] Payment process failed:', error);
      setErrors({ payment: error.message });
      if (onError) {
        onError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const paymentAmount = paymentType === 'PARTIAL' ? '1.00' : amount;

  return (
    <div className="square-payment-form max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Payment Details</h3>
        <div className="bg-blue-50 p-3 rounded-md">
          <p className="text-lg font-medium text-blue-800">
            Amount: ${paymentAmount}
            {paymentType === 'PARTIAL' && (
              <span className="text-sm text-blue-600 block">
                (Authorization charge - will be adjusted to final amount)
              </span>
            )}
          </p>
        </div>
      </div>

      {errors.init && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700 text-sm">{errors.init}</p>
        </div>
      )}

      {savedCards.length > 0 && (
        <div className="mb-6">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Payment Method</h4>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                name="paymentMethod"
                value="new"
                checked={paymentMethod === 'new'}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="mr-2"
              />
              <span>Use new card</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="paymentMethod"
                value="saved"
                checked={paymentMethod === 'saved'}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="mr-2"
              />
              <span>Use saved card</span>
            </label>
          </div>
        </div>
      )}

      {paymentMethod === 'saved' && savedCards.length > 0 && (
        <div className="mb-6">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Saved Cards</h4>
          <select 
            value={selectedCardId} 
            onChange={(e) => setSelectedCardId(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a saved card</option>
            {savedCards.map(card => (
              <option key={card.creditcard_id} value={card.creditcard_id}>
                {card.card_number_masked} - {card.card_type}
              </option>
            ))}
          </select>
        </div>
      )}

      {paymentMethod === 'new' && (
        <div className="mb-6">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Card Details</h4>
          
          {/* Square Card Elements Container */}
          <div className="space-y-4">
            {/* Card Number Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Card Number
              </label>
              <div 
                id="card-container" 
                className="min-h-[48px] border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all duration-200"
              ></div>
            </div>

            {/* Expiry and CVV Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiry Date
                </label>
                <div 
                  id="expiry-container" 
                  className="min-h-[48px] border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all duration-200"
                ></div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CVV
                </label>
                <div 
                  id="cvv-container" 
                  className="min-h-[48px] border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all duration-200"
                ></div>
              </div>
            </div>

            {/* Postal Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Postal Code
              </label>
              <div 
                id="postal-code-container" 
                className="min-h-[48px] border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all duration-200"
              ></div>
            </div>
          </div>

          {/* Security Features */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
            <div className="flex items-center text-sm text-gray-600">
              <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span>Your payment information is encrypted and secure</span>
            </div>
          </div>

          {/* Test Cards Info for Sandbox */}
          {SQUARE_CONFIG.environment === 'sandbox' && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h5 className="text-sm font-medium text-blue-800 mb-2">Test Cards (Sandbox Mode)</h5>
              <div className="text-xs text-blue-700 space-y-1">
                <p><strong>Visa:</strong> 4111 1111 1111 1111</p>
                <p><strong>Mastercard:</strong> 5555 5555 5555 4444</p>
                <p><strong>Declined:</strong> 4000 0000 0000 0002</p>
                <p className="text-blue-600">Use any future expiry date and any 3-4 digit CVV</p>
              </div>
            </div>
          )}
        </div>
      )}

      {errors.payment && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700 text-sm">{errors.payment}</p>
        </div>
      )}

      <button 
        onClick={handlePayment} 
        disabled={isLoading || (paymentMethod === 'new' && !card)}
        className={`w-full py-3 px-4 rounded-md font-medium text-white transition-colors ${
          isLoading || (paymentMethod === 'new' && !card)
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
        }`}
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            Processing...
          </div>
        ) : (
          `Pay $${paymentAmount}`
        )}
      </button>

      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>🔒 Your payment information is secure and encrypted</p>
        <p>Powered by Square</p>
      </div>
    </div>
  );
};

export default SquarePayment;
